import { create } from 'zustand';
import { shiftIndexMapAfterDelete } from '@/lib/reindexMap';

interface VideoSlice {
  blobs: Record<number, Blob | null>;
  durations: Record<number, number>;
  sources: Record<number, string>; // blob: URLs — ephemeral per-session

  setBlob(idx: number, blob: Blob | null): void;
  setDuration(idx: number, duration: number): void;
  setSource(idx: number, src: string): void;
  clear(idx: number): void;
  /** Re-key all three maps after the custom asset at `deletedIdx` is removed,
   *  so the video attached to a trailing asset follows it into its new
   *  combined index instead of the one that shifted down. Revokes the deleted
   *  asset's own blob source; kept sources are re-keyed, not revoked. */
  reindexAfterDelete(deletedIdx: number): void;
  reset(): void;
}

export const useVideoStore = create<VideoSlice>((set) => ({
  blobs: {},
  durations: {},
  sources: {},

  setBlob: (idx, blob) => set((s) => ({ blobs: { ...s.blobs, [idx]: blob } })),
  setDuration: (idx, duration) =>
    set((s) => ({ durations: { ...s.durations, [idx]: duration } })),
  setSource: (idx, src) => set((s) => ({ sources: { ...s.sources, [idx]: src } })),

  clear: (idx) =>
    set((s) => {
      const blobs = { ...s.blobs };
      const durations = { ...s.durations };
      const sources = { ...s.sources };
      const oldSrc = sources[idx];
      if (oldSrc && oldSrc.startsWith('blob:')) URL.revokeObjectURL(oldSrc);
      delete blobs[idx];
      delete durations[idx];
      delete sources[idx];
      return { blobs, durations, sources };
    }),

  reindexAfterDelete: (deletedIdx) =>
    set((s) => {
      const oldSrc = s.sources[deletedIdx];
      if (oldSrc && oldSrc.startsWith('blob:')) URL.revokeObjectURL(oldSrc);
      return {
        blobs: shiftIndexMapAfterDelete(s.blobs, deletedIdx),
        durations: shiftIndexMapAfterDelete(s.durations, deletedIdx),
        sources: shiftIndexMapAfterDelete(s.sources, deletedIdx),
      };
    }),

  reset: () =>
    set((s) => {
      for (const src of Object.values(s.sources)) {
        if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
      }
      return { blobs: {}, durations: {}, sources: {} };
    }),
}));
